import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../config/msalConfig';
import { authApi } from '../../services/api';
import useStore from '../../store';

export default function AuthCallback() {
  const { instance, inProgress } = useMsal();
  const { setToken, fetchMe }    = useStore();
  const navigate                 = useNavigate();
  const [status, setStatus]      = useState('Processing sign-in...');
  const [error, setError]        = useState(null);
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    // Wait until MSAL is not in the middle of something
    if (inProgress !== InteractionStatus.None) {
      setStatus('Communicating with Microsoft...');
      return;
    }

    // Only process once
    if (processed) return;
    setProcessed(true);

    const handleCallback = async () => {
      try {
        setStatus('Processing sign-in...');

        // Handle the redirect response
        const result = await instance.handleRedirectPromise();

        if (!result) {
          // Check if user is already logged in
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            // User already has a session, get token silently
            setStatus('Getting your profile...');
            const silentResult = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            await exchangeToken(silentResult.accessToken);
          } else {
            // No result and no accounts - go back to login
            navigate('/login', { replace: true });
          }
          return;
        }

        // We have a result from the redirect
        setStatus('Verifying your account...');
        await exchangeToken(result.accessToken);

      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Sign-in failed. Please try again.');
      }
    };

    const exchangeToken = async (accessToken) => {
      setStatus('Setting up your account...');
      const response = await authApi.spaCallback(accessToken);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Authentication failed');
      }

      setToken(response.data.token);
      setStatus('Almost there...');
      await fetchMe();
      navigate('/dashboard', { replace: true });
    };

    handleCallback();
  }, [inProgress]);

  if (error) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0A0612',
        color: '#fff', gap: 16, padding: 24, textAlign: 'center',
      }}>
        <div style={{ fontSize: '2rem' }}>⚠️</div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Sign-in failed</h2>
        <p style={{ color: 'rgba(255,255,255,.5)', maxWidth: 400 }}>{error}</p>
        <button
          onClick={() => navigate('/login')}
          style={{
            marginTop: 8, padding: '10px 24px',
            background: '#7C3AED', color: '#fff',
            border: 'none', borderRadius: 10,
            fontFamily: 'inherit', fontSize: '0.875rem',
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0A0612',
      color: '#9F67FF', gap: 16,
    }}>
      <div style={{
        width: 48, height: 48,
        border: '3px solid rgba(124,58,237,.3)',
        borderTop: '3px solid #7C3AED',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>{status}</p>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}